import React from "react";
import { motion } from "framer-motion";
import { useT } from "@/lib/i18n";
import { Star, Coins, TrendingUp, TrendingDown, BadgeCheck, ShieldOff } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

export default function Rewards() {
    const { t } = useT();
    const points = t("rewards.points") || [];
    const penalties = t("rewards.penalties") || [];
    const credits = t("rewards.credits") || [];
    const tiers = t("rewards.tiers") || [];
    const principles = t("rewards.principles") || [];

    return (
        <section
            id="ricompense"
            data-testid="sn-rewards"
            className="relative bg-sn-black text-sn-silver py-28 md:py-44 overflow-hidden"
        >
            <div className="absolute inset-0 hero-radial opacity-60" />
            <div className="absolute inset-0 hero-grid opacity-40" />
            <div className="grain" />

            <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="flex items-baseline gap-4 mb-14 md:mb-20"
                >
                    <span className="font-mono-e text-[11px] uppercase tracking-[0.3em] text-sn-green">
                        {t("rewards.eyebrow")}
                    </span>
                    <span className="hairline-dark flex-1" />
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start mb-16">
                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.9, ease: EASE }}
                        className="lg:col-span-7 font-serif text-5xl md:text-7xl leading-[0.95] tracking-[-0.02em]"
                    >
                        {t("rewards.claim_a")}{" "}
                        <em className="italic text-sn-green">{t("rewards.claim_b")}</em>.
                    </motion.h2>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
                        className="lg:col-span-5"
                    >
                        <p className="text-lg text-sn-fog leading-relaxed">
                            {t("rewards.intro")}
                        </p>
                        <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-sn-green/40 bg-sn-green/10">
                            <ShieldOff size={14} className="text-sn-green" />
                            <span className="font-mono-e text-[10px] uppercase tracking-[0.2em] text-sn-green">
                                {t("rewards.noFiatBadge")}
                            </span>
                        </div>
                        <p className="mt-4 text-sm text-sn-fog/80 leading-relaxed">
                            {t("rewards.noFiat")}
                        </p>
                    </motion.div>
                </div>

                {/* Two currency cards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
                    {/* Civic Points */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.9, ease: EASE }}
                        className="rounded-md border border-white/10 bg-white/[0.02] p-7 md:p-9"
                    >
                        <div className="flex items-center gap-3 mb-2">
                            <Star size={18} className="text-sn-blue" />
                            <h3 className="font-serif text-3xl md:text-4xl">
                                {t("rewards.pointsTitle")}
                            </h3>
                        </div>
                        <p className="text-sn-fog text-sm mb-6">
                            {t("rewards.pointsSubtitle")}
                        </p>
                        <ul className="divide-y divide-white/10 border-t border-white/10">
                            {points.map((p, i) => (
                                <li key={i} className="flex items-center justify-between gap-4 py-3">
                                    <span className="text-sn-silver/90 text-sm md:text-base flex items-center gap-2">
                                        <TrendingUp size={14} className="text-sn-green shrink-0" />
                                        {p.a}
                                    </span>
                                    <span className="font-mono-e text-sm text-sn-green shrink-0">
                                        {p.b}
                                    </span>
                                </li>
                            ))}
                        </ul>

                        <div className="mt-7 font-mono-e text-[10px] uppercase tracking-[0.24em] text-sn-fog mb-3">
                            {t("rewards.penaltiesTitle")}
                        </div>
                        <ul className="divide-y divide-white/5 border-t border-white/10">
                            {penalties.map((p, i) => (
                                <li key={i} className="flex items-center justify-between gap-4 py-3">
                                    <span className="text-sn-fog text-sm flex items-center gap-2">
                                        <TrendingDown size={14} className="text-red-400/70 shrink-0" />
                                        {p.a}
                                    </span>
                                    <span className="font-mono-e text-sm text-red-400/80 shrink-0">
                                        {p.b}
                                    </span>
                                </li>
                            ))}
                        </ul>
                        <p className="mt-6 text-xs text-sn-fog/70 leading-relaxed italic">
                            {t("rewards.severe")}
                        </p>
                    </motion.div>

                    {/* Credits */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
                        className="rounded-md border border-white/10 bg-white/[0.02] p-7 md:p-9"
                    >
                        <div className="flex items-center gap-3 mb-2">
                            <Coins size={18} className="text-sn-green" />
                            <h3 className="font-serif text-3xl md:text-4xl">
                                {t("rewards.creditsTitle")}
                            </h3>
                        </div>
                        <p className="text-sn-fog text-sm mb-6">
                            {t("rewards.creditsSubtitle")}
                        </p>
                        <ul className="space-y-4">
                            {credits.map((c, i) => (
                                <li key={i} className="flex items-start gap-3 text-sn-silver/90 text-sm md:text-base">
                                    <BadgeCheck size={16} className="text-sn-green mt-0.5 shrink-0" strokeWidth={1.8} />
                                    <span>{c}</span>
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                </div>

                {/* Climax tiers */}
                <div className="mt-14">
                    <div className="font-mono-e text-[11px] uppercase tracking-[0.28em] text-sn-fog mb-6">
                        {t("rewards.tiersTitle")}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        {tiers.map((tier, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.7, delay: i * 0.07, ease: EASE }}
                                className={`rounded-md p-5 border ${i === 4 ? "border-sn-green/50 bg-sn-green/[0.06]" : "border-white/10 bg-white/[0.02]"}`}
                            >
                                <div className="font-mono-e text-[10px] tracking-[0.2em] text-sn-fog uppercase">
                                    Lv{i + 1}
                                </div>
                                <div className="font-serif italic text-2xl mt-1 text-sn-silver">
                                    {tier.name}
                                </div>
                                <div className="mt-3 font-mono-e text-lg text-sn-green">
                                    {tier.th}
                                </div>
                                <div className="font-mono-e text-[9px] tracking-widest text-sn-fog uppercase">
                                    {t("rewards.tierColThreshold")}
                                </div>
                                <p className="mt-4 text-xs text-sn-fog leading-relaxed border-t border-white/10 pt-3">
                                    {tier.perk}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Principles */}
                <div className="mt-16">
                    <div className="font-mono-e text-[11px] uppercase tracking-[0.28em] text-sn-fog mb-6">
                        {t("rewards.principlesTitle")}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {principles.map((p, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.7, delay: i * 0.07, ease: EASE }}
                                className="border-t border-white/15 pt-5"
                            >
                                <div className="font-serif text-xl text-sn-silver mb-2">
                                    {p.t}
                                </div>
                                <p className="text-sm text-sn-fog leading-relaxed">
                                    {p.d}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
